// TrackQuizPage — for the "After 10th / Inter" track.
// 4-question stream quiz → maps to Science/Commerce/Arts/Diploma → 3-5 next steps.

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, ArrowLeft, Sparkles, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import Navbar from '../components/Navbar';
import { useAuth } from '../lib/auth';
import { fetchMyTrack, submitInterest } from '../lib/tracksApi';

const QUIZ_QUESTIONS = [
  {
    name: 'subjects',
    label: 'Which 10th subjects feel easiest to you?',
    options: ['Maths & Science', 'Social & Languages', 'Commerce & Business basics', 'Art / Music / Sports'],
  },
  {
    name: 'work_style',
    label: 'How do you prefer to work?',
    options: ['Solve puzzles / logic / math', 'Read, write, debate', 'Buy/sell, money, deals', 'Build / fix things with hands'],
  },
  {
    name: 'time_horizon',
    label: 'How soon do you want to start earning?',
    options: ['After Inter (1–2 yrs)', 'After degree (3–4 yrs)', 'After post-grad (5–6 yrs)', "I'll decide later"],
  },
  {
    name: 'parents_view',
    label: 'What do your parents say?',
    options: ['Strongly prefer engineering/medicine', 'Want a govt job', 'OK with whatever I choose', 'No strong opinion'],
  },
];

const STREAM_REC = {
  'Maths & Science|Solve puzzles / logic / math': [
    { title: 'MPC (Maths, Physics, Chemistry) → B.Tech', link: '/pathways/after-10th', why: 'Strong logical mind — engineering is a fit' },
    { title: 'BiPC → MBBS / BPharm / BSc', link: '/pathways/after-10th', why: 'Science-heavy path — biology + chemistry' },
    { title: 'Diploma in Engineering after 10th', link: '/explore/careers-ts-ap', why: 'Faster route to job — 3-yr diploma' },
  ],
  'Commerce & Business basics|Buy/sell, money, deals': [
    { title: 'MEC / CEC → B.Com → CA / MBA', link: '/pathways/after-10th', why: 'Money-minded — commerce is your zone' },
    { title: 'Banking sector careers', link: '/explore/careers-ts-ap/banking', why: 'Stable, respected, exam-based entry' },
    { title: 'Entrepreneurship pathway', link: '/explore/entrepreneurship', why: 'Build your own thing early' },
  ],
  'Social & Languages|Read, write, debate': [
    { title: 'HEC → BA → Civils / Law / Journalism', link: '/pathways/after-10th', why: 'Arts stream — Civils + Law are top paths' },
    { title: 'Mass Communication / Journalism', link: '/previews', why: 'Writing + storytelling for a living' },
  ],
  'Art / Music / Sports|Build / fix things with hands': [
    { title: 'ITI / Skilled trade pathway', link: '/explore/careers-ts-ap', why: 'Hands-on careers — earn fast, dignified' },
    { title: 'Diploma in Design / Fine Arts', link: '/previews', why: 'Creative careers — design + visual' },
  ],
};

function pickRecommendations(answers) {
  const key = `${answers.subjects}|${answers.work_style}`;
  if (STREAM_REC[key]) return STREAM_REC[key];
  // fallback: pick by subjects only
  if (answers.subjects === 'Maths & Science') return STREAM_REC['Maths & Science|Solve puzzles / logic / math'];
  if (answers.subjects === 'Commerce & Business basics') return STREAM_REC['Commerce & Business basics|Buy/sell, money, deals'];
  if (answers.subjects === 'Social & Languages') return STREAM_REC['Social & Languages|Read, write, debate'];
  return STREAM_REC['Art / Music / Sports|Build / fix things with hands'];
}

export default function TrackQuizPage() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [trackData, setTrackData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [answers, setAnswers] = useState({});
  const [error, setError] = useState('');

  useEffect(() => {
    if (user === null) return;
    if (user === false) { navigate('/signin', { replace: true }); return; }
    if (user.is_admin) { navigate('/dashboard', { replace: true }); return; }
    (async () => {
      try {
        const data = await fetchMyTrack();
        if (!data.selected_track) { navigate('/onboarding/track', { replace: true }); return; }
        if (!data.track_video_watched) { navigate('/onboarding/video', { replace: true }); return; }
        if (data.track?.post_signup !== 'quiz') { navigate('/onboarding/form', { replace: true }); return; }
        setTrackData(data);
      } finally {
        setLoading(false);
      }
    })();
  }, [user, navigate]);

  const pick = (name, value) => setAnswers((p) => ({ ...p, [name]: value }));

  const validate = () => {
    for (const q of QUIZ_QUESTIONS) if (!answers[q.name]) return `Answer: ${q.label}`;
    return '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const v = validate();
    if (v) { setError(v); return; }
    setSubmitting(true);
    setError('');
    try {
      const recs = pickRecommendations(answers);
      // Send full answers + the precomputed recommendations as a key under answers.
      // Backend matcher returns [] for after-10th — we store our own recs via the
      // recommendations endpoint via submitInterest body too.
      await submitInterest({ ...answers, __recommendations: recs });
      await refresh();
      navigate('/dashboard', { replace: true });
    } catch {
      setError('Could not save. Try again.');
      setSubmitting(false);
    }
  };

  if (loading || user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-teal-50 to-blue-50">
        <Loader2 className="w-10 h-10 animate-spin text-teal-600"/>
      </div>
    );
  }
  if (!trackData) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-blue-50">
      <Navbar/>
      <div className="pt-28 pb-16 px-4">
        <div className="max-w-2xl mx-auto">
          <button onClick={() => navigate('/onboarding/video')} className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900 mb-4">
            <ArrowLeft className="w-4 h-4"/> Back to video
          </button>
          <div className="text-center mb-6">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 text-blue-700 text-xs font-medium mb-4">
              <Sparkles size={12}/> 4-question stream quiz
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">After 10th — your starter quiz</h1>
            <p className="text-gray-600">Answer honestly. We'll map this to a stream + 3 next steps.</p>
          </div>

          <form onSubmit={handleSubmit} className="bg-white border border-gray-200 rounded-3xl shadow-xl p-6 md:p-8 space-y-6" data-testid="track-quiz-form">
            {QUIZ_QUESTIONS.map((q, i) => (
              <div key={q.name} data-testid={`quiz-q-${q.name}`}>
                <Label className="text-sm font-semibold text-gray-900 mb-2 block">
                  <span className="text-blue-600 mr-1">{i + 1}.</span> {q.label}
                </Label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
                  {q.options.map((opt) => {
                    const selected = answers[q.name] === opt;
                    return (
                      <button
                        key={opt}
                        type="button"
                        onClick={() => pick(q.name, opt)}
                        className={`text-left px-4 py-3 rounded-xl border text-sm transition-all ${selected ? 'border-blue-600 bg-blue-50 ring-1 ring-blue-300 font-semibold text-blue-900' : 'border-gray-200 hover:border-gray-300'}`}
                        data-testid={`quiz-q-${q.name}-opt-${opt.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                      >
                        {selected && <Check className="inline w-4 h-4 mr-1 text-blue-600"/>}
                        {opt}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}

            {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-3">{error}</div>}

            <div className="flex justify-end">
              <Button type="submit" disabled={submitting} className="btn-primary" size="lg" data-testid="track-quiz-submit-btn">
                {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin"/> : <Sparkles className="w-4 h-4 mr-2"/>}
                See my result
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
